//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import { IUnisonTheme } from '@bvengineering/unison-core';
import React from 'react';
import styled, { AnyStyledComponent, StyledComponent } from 'styled-components';

type TrackingId = string;

const TRACKING_ATTR = 'data-tracking-id';
const TESTING_ATTR = 'data-testid';

export interface ITrackableComponent {
  trackingId?: TrackingId;
}

/**
 * This util injects an attribute we use to create deterministic
 * selectors in tracking services like Pendo. In order to support
 * tracking for custom components, the component must include
 * the ITrackableComponent props and pass the trackingId prop down
 * into a styled component.
 *
 * @returns A component with a required trackingId prop that will
 * include a tracking-specific attribute in the DOM
 *
 * @example Add tracking support to a Styled Component
 * const TrackedDiv = withTracking(styled.div``);
 *
 * <TrackedDiv trackingId={ 'some-tracking-id' }>...</TrackedDiv>
 *
 *
 * @example Add tracking support to a Function Component
 *
 * const MyFunctionComponent: React.FunctionComponent<ITrackableComponent> = ({
 *   className,
 *   trackingId
 * }) => {
 *   return (
 *     <TrackedWrapper className={ className } trackingId={ trackingId }>
 *       ...
 *     </TrackedWrapper>
 * }
 *
 * const TrackedWrapper = withTracking(styled.div``);
 */
export function withTracking<SC extends AnyStyledComponent>(
  component: SC,
): SC extends StyledComponent<infer C, infer T, infer O, infer A>
  ? StyledComponent<C, T, O & ITrackableComponent, A>
  : never;

export function withTracking<
  CT extends keyof JSX.IntrinsicElements | React.ComponentType<unknown>,
>(component: CT): StyledComponent<CT, IUnisonTheme, ITrackableComponent>;

export function withTracking<
  C extends
    | AnyStyledComponent // eslint-disable-line @typescript-eslint/no-redundant-type-constituents
    | keyof JSX.IntrinsicElements
    | React.ComponentType<unknown>,
>(component: C): unknown {
  return styled(component).attrs<ITrackableComponent>(
    ({ trackingId }: ITrackableComponent) => ({
      [TRACKING_ATTR]: trackingId,
      [TESTING_ATTR]: trackingId,
    }),
  )<ITrackableComponent>``;
}
